import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast, { Toaster } from 'react-hot-toast';
import { formatIcon } from '../../../../../utilities/formatters';
import Page from '../../../../../components/Page';
import PrimaryButton from '../../../../../components/PrimaryButton';
import Breadcrumb from '../../../../../components/Breadcrumb';
import client from '../../../../../lib/feathers';

export default function AccommodationsEdit() {
  const { aid } = useParams();
  const [accommodation, setAccommodation] = useState();
  const [name, setName] = useState({});
  const [type, setType] = useState({});
  const [iconId, setIconId] = useState({});
  const [browsable, setBrowsable] = useState(false);
  const [collectionLabel, setCollectionLabel] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [reach, setReach] = useState(null);
  const navigate = useNavigate();

  const getAccommodation = async (uuid) => {
    try {
      const service = client.service('admin/accommodation');

      const result = await service.find({
        query: {
          id: {
            $in: [uuid],
          },
        },
      });
      const d = result.data[0];
      setName(d.name);
      setType(d.type);
      setIconId(d.iconId);
      setBrowsable(Boolean(d.browsable));
      setCollectionLabel(d.collectionLabel || '');
      setCollectionDescription(d.collectionDescription || '');
      setAccommodation(d);
    } catch (e) {
      console.log('error', e);
    }
  };

  // How many appellations and regions would actually have a collection page
  // if this were switched on. Enabling an amenity with reach of 1 publishes a
  // browse path that dead-ends.
  const getReach = async (uuid) => {
    try {
      const result = await client.service('admin/accommodation-reach').find({
        query: { threshold: 5 },
      });
      setReach(result.data.find((r) => r.accommodationId === uuid) || null);
    } catch (e) {
      console.log('error loading reach', e);
    }
  };

  useEffect(() => {
    if (aid) {
      getAccommodation(aid);
      getReach(aid);
    }
  }, [aid]);

  const onFormSubmit = async (event) => {
    event.preventDefault();
    const service = client.service('admin/accommodation');
    const formattedIcon = formatIcon(iconId);
    if (!icons[formattedIcon]) {
      toast.error('invalid Icon');
      return;
    }
    // service.update is a PUT: feathers-sequelize nulls every column absent
    // from the payload, so each collection field is named here rather than
    // left to ride along on the spread.
    const data = {
      ...accommodation,
      name,
      type,
      iconId: formattedIcon,
      browsable,
      collectionLabel: collectionLabel.trim() || null,
      collectionDescription: collectionDescription.trim() || null,
    };
    try {
      const result = await service.update(accommodation.id, data);
      console.log('result updated', result);
      toast.success('Saved!');
      getAccommodation(aid);
    } catch (e) {
      console.log('error updating accommodation', e);
      toast.error('Something went wrong creating the new listing.');
    }
  };

  const deleteAccommodation = async () => {
    const service = client.service('admin/accommodation');
    try {
      await service.remove(aid);
      toast.success('Deleted');
      navigate('/admin/data-management/accommodations');
    } catch (e) {
      console.log('error deleting accommodation', e);
      toast.error('Something went wrong deleting the listing.');
    }
  };

  const saveToolBar = () => (
    <div className="flex justify-between py-4">
      <div>
        <PrimaryButton onClick={() => deleteAccommodation()} type="button">
          Delete
        </PrimaryButton>
      </div>
      <div className="flex justify-end">
        <Link to="/admin/data-management/accommodations" type="button" className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          Cancel
        </Link>
        <PrimaryButton type="submit">
          Save
        </PrimaryButton>
      </div>
    </div>
  );

  return (
    <Page>
      <Toaster
        toastOptions={{
          duration: 3000,
        }}
      />
      <div>
        {accommodation ? (
          <div>
            <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
              <nav className="flex" aria-label="Breadcrumb">
                <Breadcrumb elements={[{ name: 'Data Management', href: '/admin/data-management' }, { name: 'Accommodations', href: '/admin/data-management/accommodations' }, { name: 'Edit Accommodation' }]} />
              </nav>
            </div>
            <form onSubmit={onFormSubmit}>
              {saveToolBar()}
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required name="name" id="name" autoComplete="false" className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type</label>
              <select type="text" value={type} onChange={(e) => setType(e.target.value)} required name="type" id="type" className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                <option disabled>Please Select</option>
                <option value="availability">Availability</option>
                <option value="safety">Safety</option>
                <option value="services">Services</option>
                <option value="certifications">Certifications</option>
              </select>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                Icon &nbsp;
                { icons[formatIcon(iconId)] ? <FontAwesomeIcon icon={icons[formatIcon(iconId)]} /> : 'invalid icon' }
              </label>
              <input type="text" value={iconId} onChange={(e) => setIconId(e.target.value)} required name="icon" id="icon" autoComplete="false" className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="text-base font-medium text-gray-900">Collection</h4>
                <p className="mt-1 text-sm text-gray-500">
                  A browsable page of wineries with this amenity, on each region and
                  appellation that has enough of them.
                </p>

                {reach && (
                  <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 p-3 text-sm">
                    <span className="font-medium text-gray-900">Reach at 5+ wineries:</span>
                    {' '}
                    <span className="text-gray-700">
                      {reach.appellationsOverThreshold}
                      {' '}
                      {reach.appellationsOverThreshold === 1 ? 'appellation' : 'appellations'}
                      {', '}
                      {reach.regionsOverThreshold}
                      {' '}
                      {reach.regionsOverThreshold === 1 ? 'region' : 'regions'}
                      {reach.largestScope > 0 && ` (largest has ${reach.largestScope})`}
                    </span>
                    {reach.scopesOverThreshold === 0 && (
                      <p className="mt-1 text-amber-700">
                        Nowhere has 5 or more of these yet, so switching this on would
                        publish an empty collection.
                      </p>
                    )}
                  </div>
                )}

                <label htmlFor="browsable" className="mt-4 flex items-start">
                  <input
                    type="checkbox"
                    id="browsable"
                    name="browsable"
                    checked={browsable}
                    onChange={(e) => setBrowsable(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="ml-2">
                    <span className="block text-sm font-medium text-gray-700">
                      Promote as a collection
                    </span>
                    <span className="block text-sm text-gray-500">
                      Needs a label below before it can appear anywhere.
                    </span>
                  </span>
                </label>

                <label htmlFor="collectionLabel" className="mt-4 block text-sm font-medium text-gray-700">
                  Public label
                </label>
                <input
                  type="text"
                  id="collectionLabel"
                  name="collectionLabel"
                  value={collectionLabel}
                  onChange={(e) => setCollectionLabel(e.target.value)}
                  placeholder={`e.g. "Pack a picnic" for ${name || 'this amenity'}`}
                  className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">
                  The page heading. Separate from the name above, which is the label
                  owners tick on their own form.
                </p>

                <div className="mt-4">
                  <span className="block text-sm font-medium text-gray-700">URL</span>
                  {accommodation.collectionSlug ? (
                    <p className="mt-1 text-sm text-gray-700">
                      <code className="bg-gray-100 px-1 py-0.5 rounded">
                        /collections/
                        {accommodation.collectionSlug}
                      </code>
                      <span className="block mt-1 text-gray-500">
                        Fixed. It is derived from the label the first time one is saved
                        and then left alone, so renaming the heading cannot break a
                        link somebody already has.
                      </span>
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">
                      Set from the label when you save.
                    </p>
                  )}
                </div>

                <label htmlFor="collectionDescription" className="mt-4 block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="collectionDescription"
                  name="collectionDescription"
                  rows={4}
                  value={collectionDescription}
                  onChange={(e) => setCollectionDescription(e.target.value)}
                  className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Shown on the collection page and used for its meta description.
                </p>
              </div>
            </form>
          </div>
        ) : <div>Loading Listing</div> }
      </div>
    </Page>
  );
}
